// The per-query audit trail (`06` §3.12, `SC-F800`–`SC-F807`, `SC-F821`–`SC-F823`).
//
// This is the **ingested** trail from a managed instance — every query the
// gateway decided on. It is not the control-plane activity feed (who changed
// what in the organization); that lives in `api/orgs.ts`, and the two are
// separate stores with separate retentions that may not even import each other
// server-side.
//
// **The types here are transcribed from the handlers, not from `06`.** The five
// routes are served by `internal/queryaudit/routes.go` and the sixth
// (`POST …/audit/verify`) by `internal/clisurface/verify.go`, and where the
// document and the code disagree the code is what answers a fetch. The three
// places that matters:
//
//   - A record's **id IS its hash**. No surrogate is minted, because interface I8
//     already makes `(instance_id, hash)` the record contract's key
//     (`queryaudit.ParseRecordID`).
//   - The list row is an **index row**, not a record: `prior_hash` and `extras`
//     are absent from the index by design and arrive only on the single-record
//     route, which fetches the raw line to get them.
//   - `chain_state` is on the **response and on every row**. `04` §4.4 forbids
//     collapsing `VERIFIED` / `GAP` / `BROKEN`, and the handler deliberately only
//     ever moves a row toward the *worse* value, so a row inside a range with a
//     hole cannot keep a green tick.
//
// Four contracts a client must honour, each of which exists because the failure
// it prevents is a wrong answer that looks like a right one:
//
//   - **Omit, do not blank** is the server's projection rule (`06` §3.12.1) and
//     the client half of it is to render [AuditProjection] whenever `applied` is
//     true. A record shown without its SQL and without a "this view is redacted"
//     affordance implies the record had no SQL.
//   - **`ingest` is mandatory on every response** and must be shown. The MVP sink
//     batches and uploads on size or interval, so a user must never mistake
//     ingest lag for a drop in traffic (`SC-S806`).
//   - **`profile` is mandatory on every response** and must be badged
//     (`SC-F821`). Under `minimized` there is no `sql_sample` *at the source*,
//     which is a different fact from "we dropped it", and under `customer_held`
//     there are no records in the cloud at all.
//   - **The filter set is an allowlist.** An unknown parameter is
//     `422 validation_failed`, not ignored: a silently dropped filter on a
//     compliance search is the wrong answer presented as the right one.

import { getJSON, newIdempotencyKey, query, sendJSON, type PageParams } from "./http.js";
import type { JobStarted } from "./jobs.js";

const base = (instanceId: string) => `/v1/instances/${encodeURIComponent(instanceId)}`;

/** AuditDecision is what the gateway did with a query
 * (`queryaudit.Decisions`, from `sluice/internal/audit/record.go`). */
export type AuditDecision = "allow" | "deny" | "reject" | "error";

/** AUDIT_DECISIONS is the closed vocabulary in the order the engine declares
 * it. Filters and legends are built from this rather than from a literal, so a
 * fifth decision is one edit. */
export const AUDIT_DECISIONS: readonly AuditDecision[] = ["allow", "deny", "reject", "error"];

/**
 * AuditEventType is the set of event types **this Sluice build actually
 * writes** (`queryaudit.EmittedEventTypes`).
 *
 * Twelve are declared upstream and eight are emitted. `policy-decision`,
 * `config-reload`, `auth-event` and `datasource-connect` are declared with no
 * writer (`SLUICE-U09`), the search route answers `422` for them, and
 * `SC-S804`'s fifth criterion forbids offering them as filters: a filter that can
 * only ever return nothing teaches a user that the trail is missing data it never
 * held.
 *
 * TODO(SLUICE-U09): there is no endpoint publishing this set, so it is
 * transcribed. `queryaudit.EmittedEventTypes` is the source of truth and the one
 * to change first.
 */
export type AuditEventType =
  | "genesis"
  | "query"
  | "query-result"
  | "admin-action"
  | "approval-requested"
  | "approval-approved"
  | "approval-rejected"
  | "approval-expired";

/** EMITTED_EVENT_TYPES is [AuditEventType] as data, in `04` §1.2's order. */
export const EMITTED_EVENT_TYPES: readonly AuditEventType[] = [
  "genesis",
  "query",
  "query-result",
  "admin-action",
  "approval-requested",
  "approval-approved",
  "approval-rejected",
  "approval-expired",
];

/**
 * AuditOrigin is the transport a record came in on. Not a closed enum
 * server-side — the filter takes any string — so this is a convenience list for
 * the picker and an unknown value still renders.
 */
export const AUDIT_ORIGINS: readonly string[] = ["rest", "mcp", "admin"];

/**
 * ChainState is `04` §4.4's three verification outcomes, and it is three rather
 * than two on purpose: the cloud copy is best-effort, so a missing record is an
 * expected, non-malicious event. A verifier with only ok/broken would report a
 * hard-killed instance as tampering.
 *
 * **Never collapse these to healthy/unhealthy.** `GAP` is reconcilable and is
 * never reported as tampering; `BROKEN` means the bytes we hold are not what the
 * writer wrote and is the one alarm in the product with no debounce.
 */
export type ChainState = "VERIFIED" | "GAP" | "BROKEN";

/**
 * AuditProfileName is the instance's audit privacy profile as the audit
 * responses spell it (`queryaudit.Profile`). It is the same vocabulary as
 * `instances.AuditProfile` and is repeated here because every audit and analytics
 * response carries it and `SC-F821` requires it badged on all of them.
 */
export type AuditProfileName = "standard" | "minimized" | "customer_held";

/** IngestState is the ingest cursor's own state (`05` §3.9). `chain_broken` is
 * terminal and alarm-raising and is deliberately distinct from `stalled`:
 * stalled means ingest is not progressing, chain_broken means what it did ingest
 * does not link. */
export type IngestState = "idle" | "running" | "stalled" | "chain_broken";

/**
 * IngestStatus is the explicit ingest watermark interface I8 requires and
 * `06` §3.12 makes mandatory on every audit response.
 *
 * It exists because of the single most likely misreading of the whole product: a
 * user reading a chart at 14:33 sees a cliff at the right edge and concludes
 * traffic stopped, when what they are seeing is the sink's upload interval.
 *
 * `lag_seconds` is reported **even when ingest is fully caught up** and must
 * never be rendered as the word "live": the transport has a floor set by the
 * sink's upload interval, so a zero would be a lie about a pipeline that batches
 * (`SC-S806`'s last criterion). Both `as_of` and `lag_seconds` are null when
 * nothing has been ingested at all, which is a third state — "not started" — and
 * not zero lag.
 */
export interface IngestStatus {
  /** The domain timestamp up to which records are complete, or null. */
  as_of: string | null;
  /** `now − as_of` in seconds, floored at zero; null when nothing is ingested. */
  lag_seconds: number | null;
  objects: number;
  records: number;
  state: IngestState;
  last_error?: string;
  /** Lag beyond `queryaudit.LagAlertThreshold` (15 minutes) — the same number
   * the operational alert uses, deliberately, so "is this normal?" is answerable
   * from the screen. */
  stale: boolean;
}

/** LAG_WARN_SECONDS mirrors `queryaudit.LagAlertThreshold` and
 * `analytics.StaleLagSeconds`. Prefer the server's own `stale` flag; this is here
 * for copy that has to name the threshold. */
export const LAG_WARN_SECONDS = 15 * 60;

/**
 * AuditProjection names what a response withheld and why. It is present whenever
 * anything was, and rendering it is not optional — see the module header.
 *
 * The two reasons need different copy because they have different remedies: one
 * is fixable by an organization owner (raise the role), the other is inherent to
 * the credential class and cannot be granted at any role.
 */
export interface AuditProjection {
  applied: boolean;
  omitted?: string[];
  reason?: "role_below_maintainer" | "credential_class_scoped";
}

/**
 * ChainPointer locates a record in the raw tier and in the chain. It is what
 * makes "view the raw line" and "verify this range" reachable from a list row.
 *
 * `state` is the chain state of the **surrounding range**, not of this record
 * alone, and it is on the row rather than only in the drawer because a user
 * filtering a range must be able to see that part of it is not verified.
 */
export interface ChainPointer {
  seq: number;
  object_key: string;
  byte_offset: number;
  /** 1-based position inside the object. Meaningful only next to `object_key` —
   * "line 412" of nothing is not a position. */
  line_no?: number;
  hash: string;
  state?: ChainState;
}

/**
 * AuditSubject is the identity block, with `06` §3.12.1's gating applied.
 *
 * `sub` always renders: it is the policy-relevant identity a denial is *about*,
 * and it is already visible to a viewer in the analytics breakdowns. `email` and
 * `groups` are projected.
 *
 * `binding` is `null` and stays null. The record's `binding_name` field is
 * declared upstream and populated by nothing in the pinned build, and returning
 * null rather than omitting the key **states** the fact instead of hiding it.
 */
export interface AuditSubject {
  sub?: string;
  issuer?: string;
  /** `jwt` or `apikey`. It is why `issuer` can legitimately be empty. */
  method?: string;
  /** Projected: maintainer + session/general key only. */
  email?: string;
  /** Projected. */
  groups?: string[];
  binding: string | null;
}

/**
 * AuditRecord is one index row — the analytic columns plus the pointer back into
 * the raw tier. It is what `GET .../audit` returns per item.
 *
 * It is **not the record**: `prior_hash` and `extras` are absent from the index
 * by design (`04` §5.3) and arrive on [AuditRecordDetail], which fetches the raw
 * line to get them.
 *
 * The optional fields are genuinely optional. `sql_sample`, `subject.email`,
 * `subject.groups` and `client_meta` are the projected ones; the rest are
 * `omitempty` on a column that is legitimately empty for some event types — a
 * `genesis` record has no decision, and only `query-result` carries `rows` and
 * `truncated`.
 */
export interface AuditRecord {
  /** The record's 64-character hash. Its identity, and the path segment the
   * single-record route takes. */
  id: string;
  instance: string;
  ts: string;
  event_type: string;
  query_id?: string;
  origin?: string;
  decision?: AuditDecision;
  error_code?: string;
  subject: AuditSubject;
  catalogs?: string[];
  tables?: string[];
  /** `kind/name` strings — the pair `SC-F809` links to a policy editor with. */
  applied_policies?: string[];
  sql_fingerprint?: string;
  rewritten_fingerprint?: string;
  /** Derived at compaction as `rewritten_fingerprint IS DISTINCT FROM
   * sql_fingerprint`. It answers "which queries did the policy set actually
   * modify?", which `applied_policies` cannot — a policy can apply and change
   * nothing. */
  rewrite_applied: boolean;
  duration_ms?: number;
  /** Populated on `query-result` only. */
  rows?: number;
  truncated: boolean;
  /** Projected, and absent by design under the `minimized` profile. */
  sql_sample?: string;
  /** Projected. Carries `remote_ip`, which holds the **edge proxy's** address on
   * every record until `SLUICE-U12` lands — see `04` §7.2. */
  client_meta?: Record<string, string>;
  message?: string;
  chain: ChainPointer;
}

/**
 * AuditFilter is the **allowlist** on `GET .../audit`, transcribed from
 * `queryaudit.filterAllowlist`. An unknown parameter is `422`, not ignored.
 *
 * Repeated values are OR within a dimension and AND across dimensions, which is
 * why the repeatable fields are arrays. `from` is inclusive and `to` is
 * exclusive; both accept RFC 3339 with an offset or a bare `YYYY-MM-DD` read as
 * midnight UTC. An **absent** range defaults to the trailing 24 hours; a
 * malformed one is refused, because a caller with an opinion must not be answered
 * about a different period.
 *
 * Two fields `04` §7.1 lists are deliberately absent, and both absences are
 * honest rather than incomplete:
 *
 *   - **`request_id`** is not in the audit record at all, so offering it would be
 *     a lie. Correlate through `query_id`.
 *   - **`sql_fingerprint`** and `rewritten_fingerprint` are on the record and are
 *     **not** in the server's allowlist, so a "find all with this shape" action
 *     would answer `422`. The drawer shows the fingerprint with a copy action and
 *     says the search is unavailable rather than offering a button that fails.
 *
 * `remote_ip` is absent for a third reason: `SLUICE-U12` is outstanding, so the
 * field holds the edge proxy's address for every record on every instance and a
 * filter over it matches everything or nothing.
 */
export interface AuditFilter extends PageParams {
  from?: string;
  to?: string;
  subject?: string[];
  issuer?: string[];
  method?: string[];
  decision?: AuditDecision[];
  error_code?: string[];
  event_type?: string[];
  catalog?: string[];
  schema?: string[];
  table?: string[];
  policy?: string[];
  query_id?: string[];
  origin?: string[];
  group?: string[];
  truncated?: boolean;
  min_duration_ms?: number;
  /** Case-insensitive substring of `sql_sample`. The one filter that must scan,
   * which is why the server caps its range at 7 days and refuses it outright
   * under the `minimized` profile — there is no text to match. */
  q?: string;
  /** Sort allowlist: `-ts` (default), `ts`, `-duration_ms`. */
  sort?: "-ts" | "ts" | "-duration_ms";
}

/** AUDIT_LIMIT_MAX is `queryaudit.MaxLimit`. `06` §1.5 raises the general
 * ceiling of 200 to 1000 on audit and analytics specifically, because an
 * export-adjacent read of a bounded range is a legitimate thousand-row request. */
export const AUDIT_LIMIT_MAX = 1000;

/** TEXT_SEARCH_WINDOW_DAYS is `queryaudit.TextSearchWindow`. A `q=` search over
 * a wider range is refused rather than served slowly. */
export const TEXT_SEARCH_WINDOW_DAYS = 7;

/**
 * AuditSearchPage is `GET .../audit`. Keyset, and deliberately without a
 * `total`: counting a growing audit index on every page is a stall the
 * single-write-connection model cannot absorb.
 *
 * `next` is **absent** on the last page (the Go field is `omitempty`), so treat
 * an empty string and an absent key alike.
 */
export interface AuditSearchPage {
  items: AuditRecord[];
  next?: string;
  projection: AuditProjection;
  profile: AuditProfileName;
  ingest: IngestStatus;
  /** The bounds actually applied. They are defaulted when absent, and a client
   * charting the result needs the window it received rather than the one it may
   * have omitted. */
  range: { from: string; to: string };
  /** The chain state over the requested range. A list that showed rows without
   * it would let a user read a gap as an absence of activity. */
  chain_state: ChainState;
}

/**
 * searchAudit searches the ingested index (`SC-F804`).
 *
 * Every response that actually includes `sql_sample` appends a control-plane
 * audit row naming the actor, the instance, the filter and the record count. This
 * is the only place in the product where a *read* is audited, and the reason is
 * exactly that it is the one read revealing another person's query text — worth
 * knowing before adding a background prefetch here.
 */
export function searchAudit(
  instanceId: string,
  filter: AuditFilter = {},
): Promise<AuditSearchPage> {
  return getJSON<AuditSearchPage>(`${base(instanceId)}/audit${query({ ...filter })}`);
}

/** HashMode is which recomputation path verified a line. `tolerant` on a record
 * that is not part of a recovered range means either a new Sluice serialization
 * or a mangled line, and `04` §10.1 alerts on it. */
export type HashMode = "byte_exact" | "tolerant";

/**
 * RecordIntegrity is the drawer's Integrity section, computed **on every detail
 * read** rather than trusted from the ingest-time walk. That is the difference
 * between "we verified this once" and "this is verified".
 */
export interface RecordIntegrity {
  /** Whether the raw line's hash recomputes to the hash it stores. */
  verified: boolean;
  mode: HashMode;
  /** Marks a record repaired through `/admin/audit/tail` and re-canonicalized
   * cloud-side. `04` §2.5's "byte-identical to what your instance wrote" claim
   * does not hold for it, and saying so is the disclosure. */
  recovered: boolean;
  object_key: string;
  line_no: number;
  byte_offset: number;
  /** The state of the **surrounding range**, not of this line. */
  chain_state: ChainState;
}

/**
 * AuditRecordDetail is `GET .../audit/{hash}` — the product's proof surface.
 *
 * Everything above `raw` is *our interpretation* of a record. `raw` is the
 * artifact: the exact bytes the instance wrote, which a customer can hand to
 * `sluice audit verify` or their own tooling and recompute by hand. That is why
 * it is fetched from object storage for every request instead of being
 * reconstructed from the index — a reconstructed line would be a claim about the
 * record rather than the record.
 */
export interface AuditRecordDetail extends AuditRecord {
  prior_hash: string;
  /** The record's free-form block, absent on most records. `extras.rewrite`'s
   * `{limit, timeout_ms, sample, policies}` is the only per-record evidence that
   * a limit, a timeout or a sampling clamp actually bit. Projected with the rest:
   * it is caller-influenced and can carry the material the projection gates. */
  extras?: Record<string, unknown>;
  /** Projected: the original wire line, byte for byte. */
  raw?: string;
  /** `sha256` over the original line, returned **instead of** `raw` when the
   * projection withholds it — enough to prove a record was not altered in
   * transit without disclosing its contents. */
  raw_digest?: string;
  integrity: RecordIntegrity;
  projection: AuditProjection;
  profile: AuditProfileName;
  ingest: IngestStatus;
}

/**
 * getAuditRecord reads one record by its hash, with its chain position and —
 * subject to the projection — the original wire line.
 *
 * A `rec_` prefix is tolerated by the server and stripped; pass the bare hash.
 */
export function getAuditRecord(
  instanceId: string,
  recordHash: string,
): Promise<AuditRecordDetail> {
  return getJSON<AuditRecordDetail>(
    `${base(instanceId)}/audit/${encodeURIComponent(recordHash)}`,
  );
}

// ───────────────────────────────────────────── the chain (SC-F803) ──

/** ChainGenesis is the anchor an auditor is handed. The seed's fingerprint **is**
 * the anchor — the anchor is defined as `sha256(seed)` — so there is no second
 * digest, and the seed itself never leaves the sealed store. */
export interface ChainGenesis {
  /** `sha256:<anchor>`. */
  seed_fingerprint: string;
  anchor_hash: string;
  /** When the anchor was recorded, which is instance creation. */
  minted: string | null;
}

/** ChainObject is one ingested object in the ledger. A `pruned` row **stays
 * forever**: the boundary hash it carries is what makes the remaining archive
 * verifiable, and deleting it would break anchored verification permanently on
 * the first retention run. */
export interface ChainObject {
  seq: number;
  state: "retained" | "pruned";
  object_key: string;
  first_prior_hash: string;
  final_hash: string;
  record_count: number;
  first_record_at: string;
  last_record_at: string;
  pruned_at: string | null;
  verified_at: string | null;
  recovered_ranges?: RecoveredRange[];
}

/** RecoveredRange is a range repaired through `/admin/audit/tail` rather than
 * read from the object. A non-empty list **qualifies** the byte-identical claim
 * for exactly those records, because the tail endpoint returns re-marshalled
 * struct JSON rather than the canonical wire line. */
export interface RecoveredRange {
  from_hash: string;
  to_hash: string;
  record_count: number;
  recovered_at: string;
  /** `admin_tail` — on the wire because it is the reason the range is disclosed
   * at all. */
  method: string;
}

/**
 * ChainSegmentSpan is one span of hash chain between genesis-class anchors
 * (`04` §4.8).
 *
 * An instance's audit is an **ordered list** of these and not one chain:
 * restarts on an empty volume, retention pruning and volume loss all cut it, and
 * there is no hash anywhere joining two spans because none was ever computed. A
 * chain with three spans is one whose hash chain restarted twice, and each span
 * verifies from its own anchor — the badge reads "3 segments, all verified"
 * rather than a single green tick.
 *
 * `started_by` is `instance_creation`, `volume_loss` or `prune_boundary`. A
 * `volume_loss` span with **no** corresponding migration or restore event is a
 * suspicious finding rather than a routine one.
 */
export interface ChainSegmentSpan {
  ordinal: number;
  genesis_anchor: string;
  first_hash: string;
  last_hash: string;
  record_count: number;
  first_record_at: string;
  last_record_at: string;
  started_by: string;
  started_by_event?: string;
}

/**
 * ChainStatus is `GET .../audit/chain` (`SC-F803`, `SC-F807`) — the artifact a
 * customer hands an auditor alongside the JSONL export.
 *
 * It is the one record-adjacent surface that answers under the `customer_held`
 * profile, which is `SC-F822`'s whole product: with no records in the cloud there
 * is still a genesis anchor, still a notarization ledger, and therefore still a
 * real integrity claim.
 */
export interface ChainStatus {
  genesis: ChainGenesis;
  /** Null until a verification has run. **"Never verified" is a distinct and
   * honest state** (`SC-S805`): showing an unverified chain as implicitly fine
   * would be the same category of dishonesty as labelling `datasource check` a
   * connection test. */
  last_verified: { hash: string; at: string } | null;
  /** The object-level ledger: one entry per ingested object. */
  segments: ChainObject[];
  /** The ordered list of hash-chain spans. */
  chain_segments: ChainSegmentSpan[];
  /** The rollup over every span. Deliberately not a boolean: collapsing `GAP`
   * into `BROKEN` would report a killed instance as tampering, and collapsing it
   * into `VERIFIED` would hide a hole. */
  continuity: ChainState;
  profile: AuditProfileName;
  ingest: IngestStatus;
}

/** getAuditChain reads the chain registry: genesis anchor, last verified hash,
 * object ledger, chain spans and the continuity rollup. */
export function getAuditChain(instanceId: string): Promise<ChainStatus> {
  return getJSON<ChainStatus>(`${base(instanceId)}/audit/chain`);
}

// ─────────────────────────────────────── verification (SC-F807) ──

/**
 * BreakClass is what a chain break actually **means**, and it exists because
 * `audit verify`'s exit 4 alone does not say.
 *
 * The four shapes have genuinely different responses, which is why they are not
 * collapsed: `invalid_json` is most often a verify that raced the file sink's
 * buffered write and read a partial final line — **not** tampering —
 * `anchor_mismatch` usually means a restored or substituted volume, `not_chained`
 * means a record was removed or inserted, and `hash_mismatch` is the one shape
 * that means a record's content was altered.
 */
export type BreakClass =
  | "invalid_json"
  | "anchor_mismatch"
  | "not_chained"
  | "hash_mismatch"
  | "unclassified";

/** ChainBreak locates and explains a verification failure. Every field is
 * evidence and none is paraphrased — render `explanation` and `advice` verbatim
 * rather than writing new copy for them here. */
export interface ChainBreak {
  /** The `*.jsonl` file name, relative to the audit directory. */
  file: string;
  /** 1-based line within that file. Unlike a validation finding's line, this one
   * is populated: the walker counts lines as it reads. */
  line: number;
  class: BreakClass;
  /** The walker's message, verbatim. */
  error: string;
  /** The customer-facing meaning of `class`, written server-side so the wording
   * cannot drift from the condition that raised it. */
  explanation: string;
  stored?: string;
  recomputed?: string;
  prior_hash?: string;
  expected?: string;
  /** Always false, and a field rather than a comment so it can be branched on:
   * `SC-S712` requires the product to attempt no repair, no re-hash and no
   * re-chain, because doing so destroys the evidentiary value that made the
   * finding worth reporting. Never build a "fix it" button. */
  repairable: boolean;
  advice: string;
}

/** VerifyScope is what was walked. `instance` is v1 and is refused today with a
 * message that says so. */
export type VerifyScope = "cloud" | "instance";

/**
 * VerifyResponse is the `audit.verify` job result, normalizing the two shapes the
 * CLI emits into one object.
 *
 * **The job succeeds in both cases** — a verification that ran and found a break
 * did its job — so `ok` is the field to branch on, and never the job status.
 *
 * `files` and `records` are **zero on a failure**: the CLI's failure object
 * carries no counters, so a client that rendered "0 records verified" beside a
 * break would imply nothing was checked. Say "not reported" instead.
 *
 * A badge built on this must state the anchor used, what was walked and when
 * (`SC-F807`, `SC-S805`). A green tick with no explanation of what was checked is
 * precisely what the compliance persona churns on.
 */
export interface VerifyResponse {
  ok: boolean;
  scope: VerifyScope;
  /** The anchor that was pinned, echoed because a verification's strength is a
   * function of it: with no anchor the walk accepts whatever the first record
   * claims as its predecessor, so a wholesale substituted chain verifies clean. */
  anchor: string;
  from?: string;
  to?: string;
  files: number;
  records: number;
  last_hash?: string;
  break: ChainBreak | null;
  /** Whether the outcome was filed with the anchor registry. False means the
   * next verification re-walks from genesis. */
  recorded: boolean;
  run_id?: string;
  duration_ms: number;
}

/**
 * verifyAuditChain runs `audit verify --anchor <genesis> --json` (`SC-F711`,
 * `SC-F807`) → job.
 *
 * `cloud` (the default) verifies the **ingested copy** in object storage —
 * proving the Cloud-side copy is faithful. `instance` is v1 and refused. Gated on
 * the `validation` feature, not `audit`: it is a proxied-CLI surface, and `audit`
 * is the *data* feature.
 */
export function verifyAuditChain(
  instanceId: string,
  input: { scope?: VerifyScope; from?: string; to?: string } = {},
): Promise<JobStarted<VerifyResponse>> {
  return sendJSON<JobStarted<VerifyResponse>>(`${base(instanceId)}/audit/verify`, "POST", input, {
    idempotencyKey: newIdempotencyKey(),
  });
}

// ────────────────────────────────────────────── export (SC-F805) ──

/** ExportFormat is the two formats of `04` §7.4. JSONL is the evidence format —
 * the raw T0 lines, byte-identical, in chain order, plus a sidecar manifest — and
 * CSV is the spreadsheet. */
export type ExportFormat = "jsonl" | "csv";

/**
 * ExportRequest is the export body.
 *
 * **A JSONL export cannot be filtered** and the server refuses the combination
 * rather than accepting it quietly: the records a filter drops are the
 * `prior_hash` of the records it keeps, so the result would verify as `BROKEN`
 * and the person holding it would be an auditor. Filter the CSV instead.
 */
export interface ExportRequest {
  format?: ExportFormat;
  from?: string;
  to?: string;
  /** CSV only. Same allowlist as the search route. */
  filter?: Record<string, string[]>;
}

/**
 * ExportResult is the `audit.export` job result: a signed, expiring download
 * link.
 *
 * TODO(SC-F805): the worker that produces this is v1, so the field set is
 * `06` §3.12's and not transcribed from a Go type. Read it defensively.
 */
export interface ExportResult {
  url?: string;
  expires?: string;
  records?: number;
  bytes?: number;
  content_type?: string;
}

/**
 * exportAudit starts an export (`SC-F805`) → job producing a signed URL.
 *
 * Step-up gated (`export_audit`) and maintainer-or-above, and refused outright to
 * every fine-grained token. This is the one action that moves a bulk copy of the
 * customer's query history — subjects, tables, SQL samples — out of the product,
 * so a caller without a grant gets `verification_required` and must earn one
 * (`api/auth.ts`'s `requestStepUpOtp` / `verifyWithPasskey`).
 */
export function exportAudit(
  instanceId: string,
  input: ExportRequest,
  opts: { verification?: string } = {},
): Promise<JobStarted<ExportResult>> {
  return sendJSON<JobStarted<ExportResult>>(`${base(instanceId)}/audit/exports`, "POST", input, {
    idempotencyKey: newIdempotencyKey(),
    verification: opts.verification,
  });
}

// ─────────────────────────────────────────── live tail (SC-F823) ──

/** TAIL_DEFAULT_N and TAIL_POLL_MS are `SC-F823`'s numbers: 200 records every 3
 * seconds. The server clamps `n` above `TAIL_MAX_N` and reports it in `capped`,
 * which the instance itself would not have told us. */
export const TAIL_DEFAULT_N = 200;
export const TAIL_MAX_N = 1000;
export const TAIL_POLL_MS = 3000;

/**
 * TailRecord is one record from the instance's own admin plane.
 *
 * It is a **distinct type** from [AuditRecord] on purpose. They look similar and
 * they are not interchangeable: an index row points at bytes in the raw tier that
 * can be verified, and a tail record points at nothing.
 */
export interface TailRecord {
  ts: string;
  event_type: string;
  query_id?: string;
  decision?: AuditDecision;
  error_code?: string;
  subject: AuditSubject;
  catalogs?: string[];
  tables?: string[];
  /** `kind/name`. */
  applied_policies?: string[];
  sql_fingerprint?: string;
  sql_sample?: string;
  duration_ms?: number;
  rows?: number;
  truncated: boolean;
  /** The de-duplication key the widget appends on. */
  hash: string;
  /** **False, always.** The bytes behind this record are re-marshalled struct
   * JSON and not the sequence the hash chain covers, so it cannot be exported,
   * verified, or used to support a chain claim. Never offer a "copy the raw line"
   * affordance on a tail record. */
  verifiable: boolean;
}

/**
 * TailResponse is `GET .../audit/tail` (`SC-F823`).
 *
 * `lossy` and `notice` are **data on the response** rather than copy in a UI
 * file, so a second client cannot forget them. Render `notice` verbatim.
 */
export interface TailResponse {
  items: TailRecord[];
  /** The count actually requested of the instance, after clamping. */
  n: number;
  capped: boolean;
  /** Always true. */
  lossy: boolean;
  /** The sentence `SC-S813` requires the surface itself to carry. */
  notice: string;
  profile: AuditProfileName;
  projection: AuditProjection;
  ingest: IngestStatus;
}

/**
 * getAuditTail reads the instance's live tail accessory (`SC-F823`). It has
 * **zero ingest lag**, which is what the tight iteration loop needs — change a
 * policy, run a query, see the decision — and its losses are irrelevant to that
 * use.
 *
 * The `?follow=true` SSE variant is deliberately not wrapped: the handler refuses
 * it explicitly rather than answering a plain page to a client that asked to
 * follow, and `SC-F823` specifies polling instead.
 */
export function getAuditTail(
  instanceId: string,
  params: { n?: number } = {},
): Promise<TailResponse> {
  return getJSON<TailResponse>(`${base(instanceId)}/audit/tail${query({ ...params })}`);
}
